import { useEffect, useMemo, useRef, useState } from 'react'
import { Emoji } from './Emoji.jsx'
import { CategoryIcon } from './CategoryIcon.jsx'
import { getAncestors, getCategoryPath, isSystemCategory } from '../categoryTree.js'
import { TopSheet } from './TopSheet.jsx'

// Custom category picker: tree dropdown with accordion expand/collapse,
// preview-highlight on tap, "Choose" button to commit. When the user types
// in the search field, falls back to a flat list of matches.
export function CategoryPicker({
  value,           // current category id (committed) or '' / null for none
  onChange,        // called with id (string) or '' on commit
  categories,      // already filtered (e.g. by type)
  placeholder = '—',
  chooseLabel = 'Choose',
  cancelLabel = 'Cancel',
  noneLabel = 'None',
  searchPlaceholder = 'Search category…',
  title = 'Category',
  closeLabel = 'Close',
}) {
  const [open, setOpen]           = useState(false)
  const [previewId, setPreviewId] = useState(value || '')
  const [expanded, setExpanded]   = useState(() => new Set())
  const [query, setQuery]         = useState('')
  const searchRef = useRef(null)

  // Le categorie di sistema (oggi "Even") non si scelgono: sono il recipiente
  // in cui ARRIVANO le spese sincronizzate. Restano visibili come valore
  // corrente — e si possono sostituire con una propria — ma non compaiono
  // tra le opzioni, dove sarebbero solo un modo per sbagliare.
  const selectable = useMemo(
    () => categories.filter(c => !isSystemCategory(c)),
    [categories],
  )

  // Build child index for tree rendering
  const childrenByParent = useMemo(() => {
    const m = new Map()
    for (const c of selectable) {
      const k = c.parentId ?? null
      if (!m.has(k)) m.set(k, [])
      m.get(k).push(c)
    }
    for (const list of m.values()) list.sort((a, b) => a.name.localeCompare(b.name))
    return m
  }, [selectable])

  const selectedCat = value ? categories.find(c => c.id === value) : null

  // When opening, sync preview with current committed value and auto-expand its ancestors
  useEffect(() => {
    if (!open) return
    setPreviewId(value || '')
    setQuery('')
    if (value) {
      const cat = categories.find(c => c.id === value)
      if (cat) {
        const ancestors = getAncestors(cat.id, categories)
        setExpanded(new Set(ancestors.map(a => a.id)))
      }
    } else {
      setExpanded(new Set())
    }
    // Auto-focus search → native keyboard opens (sheet shrinks accordingly)
    setTimeout(() => searchRef.current?.focus(), 0)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Outside-click + Esc dismiss handled by TopSheet.

  const isSearching = query.trim().length > 0
  const filtered = useMemo(() => {
    if (!isSearching) return []
    const q = query.trim().toLowerCase()
    return selectable.filter(c => c.name.toLowerCase().includes(q))
  }, [selectable, query, isSearching])

  function handleRowClick(cat) {
    setPreviewId(cat.id)
    if (childrenByParent.has(cat.id)) {
      setExpanded(prev => {
        const next = new Set(prev)
        if (next.has(cat.id)) next.delete(cat.id)
        else next.add(cat.id)
        return next
      })
    }
  }

  function commit() {
    onChange(previewId || '')
    setOpen(false)
  }

  // Render the tree recursively
  function renderRows(parentId, depth) {
    const rows = childrenByParent.get(parentId) ?? []
    return rows.map(cat => {
      const hasChildren = childrenByParent.has(cat.id)
      const isExpanded  = expanded.has(cat.id)
      const isPreview   = previewId === cat.id
      return (
        <div key={cat.id} className="cp-branch">
          <button
            type="button"
            className={`cp-row${isPreview ? ' preview' : ''}`}
            style={{ paddingLeft: `${0.5 + depth * 1.1}rem` }}
            onClick={() => handleRowClick(cat)}
          >
            <span className="cp-chevron" aria-hidden="true">
              {hasChildren ? (isExpanded ? '▾' : '▸') : '·'}
            </span>
            <span className="cat-icon"><Emoji symbol={cat.icon} size="1.2rem" /></span>
            <span className="cp-name">{cat.name}</span>
          </button>
          {hasChildren && isExpanded && renderRows(cat.id, depth + 1)}
        </div>
      )
    })
  }

  return (
    <div className="cp-wrap">
      <button
        type="button"
        className={`cp-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {selectedCat ? (
          <>
            <CategoryIcon category={selectedCat} size="1.1rem" />
            <span className="cp-trigger-label">
              {getCategoryPath(selectedCat, categories)}
            </span>
          </>
        ) : (
          <span className="cp-trigger-placeholder">{placeholder}</span>
        )}
        <span className="cp-trigger-caret" aria-hidden="true">▾</span>
      </button>

      <TopSheet open={open} onClose={() => setOpen(false)} className="cp-sheet">
        <div className="cs-header">
          <span className="cs-h-title">{title}</span>
          <button
            type="button"
            className="cs-h-close"
            onClick={() => setOpen(false)}
            aria-label={closeLabel}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <input
          ref={searchRef}
          type="search"
          className="cp-search"
          placeholder={searchPlaceholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div className="cp-list" role="listbox">
          {!isSearching && (
            <button
              type="button"
              className={`cp-row${!previewId ? ' preview' : ''}`}
              onClick={() => setPreviewId('')}
            >
              <span className="cp-chevron" aria-hidden="true">·</span>
              <span className="cp-name cp-none">{noneLabel}</span>
            </button>
          )}
          {isSearching
            ? filtered.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  className={`cp-row${previewId === cat.id ? ' preview' : ''}`}
                  onClick={() => setPreviewId(cat.id)}
                >
                  <span className="cp-chevron" aria-hidden="true">·</span>
                  <span className="cat-icon"><Emoji symbol={cat.icon} size="1.2rem" /></span>
                  <span className="cp-name">{getCategoryPath(cat, categories)}</span>
                </button>
              ))
            : renderRows(null, 0)
          }
        </div>
        <div className="cp-actions">
          <button type="button" className="btn" onClick={() => setOpen(false)}>
            {cancelLabel}
          </button>
          <button type="button" className="btn btn-primary" onClick={commit}>
            {chooseLabel}
          </button>
        </div>
      </TopSheet>
    </div>
  )
}
